import { useState, useEffect, ChangeEvent, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import PunctualCourseRepository from "../../../Repositories/PunctualCourseRepository";
import { HeadCell } from "../../../Objects/interfaces/TableTypes";
import { capitalizeFirstLetter } from "../../../Utils/Function";
import { getActivePrestataireBrandConfig } from "../../../config/environment.config";

export type ClientCourseType = "avenir" | "historique";

/**
 * Espace client : données de la liste des cours (à venir ou historique),
 * miroir de useListCourse côté prof. Pagination et filtres (intervenant,
 * statut URSSAF, période) portés par le serveur.
 */
export function useClientListCourse(type: ClientCourseType) {
    const client = useSelector((state: any) => state?.user?.currentUser?.client);
    const clientId: number | undefined = client?.id;

    // ── Pagination ────────────────────────────────────────────────────────────
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // ── Filtres ───────────────────────────────────────────────────────────────
    const [showFilters, setShowFilters] = useState(false);
    const [nameSearch, setNameSearch] = useState("");
    const [statusSearch, setStatusSearch] = useState("");
    const [dateValues, setDateValues] = useState<any[]>([]);
    const [startDateSearch, setStartDateSearch] = useState("");
    const [endDateSearch, setEndDateSearch] = useState("");

    // ── Dialog détail cours ───────────────────────────────────────────────────
    const [selectedCourse, setSelectedCourse] = useState<any | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);

    useEffect(() => {
        const formatted: string[] = [];
        dateValues.forEach((date: any) => {
            formatted.push(moment(date?.toDate?.()).format("YYYY-MM-DD"));
        });
        setStartDateSearch(formatted[0] ? `${formatted[0]} 00:00:00` : "");
        setEndDateSearch(formatted[1] ? `${formatted[1]} 23:59:59` : "");
        setPage(0);
    }, [dateValues]);

    const { data, isLoading } = useQuery({
        queryKey: [
            "clientCourses",
            type,
            clientId,
            page,
            rowsPerPage,
            nameSearch,
            statusSearch,
            startDateSearch,
            endDateSearch,
        ],
        queryFn: () =>
            PunctualCourseRepository.getClientDashboardCourses(
                clientId!,
                type,
                page,
                rowsPerPage,
                nameSearch,
                statusSearch,
                startDateSearch,
                endDateSearch
            ),
        enabled: !!clientId,
    });

    const courses: any[] = data?.courses ?? [];
    const totalItems: number = data?.totalItems ?? 0;

    const headCells: HeadCell[] = useMemo(() => [
        { id: 1, numeric: false, disable: false, align: "left", label: `👤 ${capitalizeFirstLetter(getActivePrestataireBrandConfig())}` },
        { id: 2, numeric: false, disable: false, align: "left", label: "📅 Date" },
        { id: 3, numeric: false, disable: false, align: "left", label: "⏱️ Durée" },
        { id: 4, numeric: false, disable: false, align: "center", label: "💰 Montant" },
        { id: 5, numeric: false, disable: false, align: "center", label: "✅ Statut" },
        { id: 6, numeric: false, disable: false, align: "center", label: "📋 Format" },
        { id: 7, numeric: false, disable: false, align: "center", label: "👁️ Détail" },
    ], []);

    const hasActiveFilters = !!(nameSearch || statusSearch || dateValues.length);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

    const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleOpenDetail = (course: any) => {
        setSelectedCourse(course);
        setDetailOpen(true);
    };

    const clearFilters = () => {
        setNameSearch("");
        setStatusSearch("");
        setDateValues([]);
        setPage(0);
    };

    return {
        client,
        clientId,
        page,
        rowsPerPage,
        showFilters, setShowFilters,
        nameSearch, setNameSearch,
        statusSearch, setStatusSearch,
        dateValues, setDateValues,
        selectedCourse, setSelectedCourse,
        detailOpen, setDetailOpen,
        isLoading,
        courses,
        totalItems,
        headCells,
        hasActiveFilters,
        handleChangePage,
        handleChangeRowsPerPage,
        handleOpenDetail,
        clearFilters,
    };
}
